import React from 'react';
import Icon from '../../../components/AppIcon';

const AIAnalysisDashboard = ({ metrics, errors, activeAnalyses, onErrorSelect, loading }) => {
  const metricCards = [
    {
      label: 'Active Analyses',
      value: metrics?.activeAnalyses || 0,
      icon: 'Activity',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      label: 'Avg Confidence',
      value: `${metrics?.avgConfidenceScore?.toFixed(1) || 0}%`,
      icon: 'Target',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      label: 'Success Rate',
      value: `${metrics?.resolutionSuccessRate?.toFixed(1) || 0}%`,
      icon: 'CheckCircle',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      label: 'Model Performance',
      value: `${metrics?.learningModelPerformance?.toFixed(1) || 0}%`,
      icon: 'TrendingUp',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    }
  ];

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-600 bg-red-50 border-red-200',
      high: 'text-orange-600 bg-orange-50 border-orange-200',
      medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      low: 'text-blue-600 bg-blue-50 border-blue-200'
    };
    return colors?.[severity] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div className="space-y-4">
      {/* Metrics Cards */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="BarChart3" className="w-5 h-5 text-purple-600" />
          AI Analysis Metrics
        </h3>
        <div className="space-y-3">
          {metricCards?.map((metric, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${metric?.bgColor} rounded-lg`}>
                  <Icon name={metric?.icon} className={`w-4 h-4 ${metric?.textColor}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{metric?.label}</span>
              </div>
              <span className={`text-lg font-bold ${metric?.textColor}`}>{metric?.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Errors for Analysis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Icon name="AlertTriangle" className="w-5 h-5 text-orange-600" />
            Recent Errors
          </h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {errors?.length || 0} total
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
        ) : errors?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <p className="text-gray-600 text-sm">No errors detected</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {errors?.slice(0, 10)?.map((error) => (
              <button
                key={error?.id}
                onClick={() => onErrorSelect?.(error)}
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded border ${getSeverityColor(error?.severity)}`}>
                    {error?.severity?.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(error?.occurredAt)?.toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 group-hover:text-purple-600">
                  {error?.errorMessage}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Icon name="Code" className="w-3 h-3" />
                  <span>{error?.errorCode || 'N/A'}</span>
                  {error?.affectedModule && (
                    <>
                      <span>•</span>
                      <span>{error?.affectedModule}</span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* AI Model Status */}
      <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg p-4 text-white">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Icon name="Brain" className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-semibold">Anthropic Claude</h4>
            <p className="text-xs text-purple-100">claude-sonnet-4-5</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span>Model Active & Learning</span>
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisDashboard;